// Agent state vocabulary. Every surface that names a runtime state reads it
// from here, so the wording never drifts between the sidebar, the profile and
// the runtime card.
//
// The agent surfaces split language deliberately: a state name comes from this
// vocabulary and is Chinese, because these labels are bound to the server's
// agent state constants and changing one means changing both, while the prose
// around them on the profile's lifecycle surfaces — the banner, the LIFECYCLE
// section, the key and connect notes — is English.
//
// Blueprint agent-lifecycle.md §2.3: a grey dot with no words is forbidden —
// offline must say it is offline, and an error must name its cause.
import type { AgentRuntimeReason, AgentRuntimeState } from './api';

export interface AgentStateLabel {
  text: string;
  tone: 'ok' | 'muted' | 'error';
}

export const REASON_LABELS: Record<AgentRuntimeReason, string> = {
  api_key_invalid: 'API key 失效',
  quota_exceeded: '已超出配额',
  network_unreachable: '网络不可达',
  runtime_crashed: 'Runtime 崩溃',
  runtime_timeout: 'Runtime 超时',
  unknown: '未知错误',
};

// DISABLED_LABEL is not a runtime state — the server forces a disabled agent's
// state to 'offline' — so it wins over describeAgentState() wherever
// Agent.disabled is set. Rendering "已离线" there would tell the owner the
// agent dropped out on its own rather than that they switched it off.
export const DISABLED_LABEL = '已禁用';

export function describeAgentState(
  state: AgentRuntimeState | undefined,
  reason: AgentRuntimeReason | undefined,
): AgentStateLabel {
  if (state === 'online') return { text: '在线', tone: 'ok' };
  if (state === 'error') {
    const reasonText = reason ? REASON_LABELS[reason] ?? reason : '未知错误';
    return { text: `故障 (${reasonText})`, tone: 'error' };
  }
  if (state === 'busy') return { text: '在工作', tone: 'ok' };
  if (state === 'idle') return { text: '空闲', tone: 'muted' };
  return { text: '已离线', tone: 'muted' };
}
